import { Button, Switch } from "@mui/material";
import dayjs from "dayjs";
import { AddressItem, reprojectAddressItem } from "../../services/GeocodeService";
import { SourceReferenceSystem, BruksklasseModulVognTog } from "../../services/RuteplanAPI";
import { EnvironmentName, useEnvironmentSettingActions } from "../../stores/EnvironmenSettingStore";
import { useRouteLocationActions } from "../../stores/RouteLocationStore";
import { BruksklasseNormalTransport, BruksklasseSpesialTransport, BruksklasseTommerTransport, RouteSettings, Veggruppe, useRouteSettingsActions } from "../../stores/RouteSettingsStore";
import { useUserNotificationActions } from "../../stores/UserNotificationStore";
import React, { useEffect } from "react";
import { StringInput } from "../common/StringInput";

export interface IExternalRouteInputProps {
}

class URLSearchParamsCaseInsensitive {

    private urlSearchParams: URLSearchParams;
    constructor(url: string) {
        const tempParams = new URLSearchParams(url.substring(url.indexOf('?')));
        this.urlSearchParams = new URLSearchParams();

        for (const [key, val] of tempParams.entries()) {
            this.urlSearchParams.append(key.toLowerCase(), val)
        }
    }

    public get(key: string): string | null {
        return this.urlSearchParams.get(key.toLowerCase());
    }

    public getToArray(key: string): string[] | null {
        return  this.urlSearchParams.getAll(key);         
    }

    public sort(): void {
        this.urlSearchParams.sort();
    }
}

function ParseRouteSettingsFromUrl(url: string): { routeSettings: RouteSettings, fromAddress: AddressItem, toAddress: AddressItem, viaPoints: Array<AddressItem>, barriers: Array<AddressItem>, environmentName: EnvironmentName } {

    const urlParams = new URLSearchParamsCaseInsensitive(url);
    urlParams.sort();

    const routeSettings = {
        avoidSituationIds: urlParams.get("AvoidSituationIds"),
        avoidFeatureInstanceIds: urlParams.get("AvoidFeatureInstanceIds"),
        avoidTrafficMessageTypes: urlParams.get("AvoidTrafficMessageTypes"),
        avoidRoadFeatureTypes: urlParams.getToArray("AvoidRoadFeatureTypes"),
        erLinkSemiTrailer: urlParams.get("ErLinkSemitrailer")?.toLowerCase() === "true",
        height: urlParams.get("Height") as unknown as number,
        includeVeggruppeIkke: urlParams.get("IncludeVeggruppeIkke")?.toLowerCase() === "true",
        includeObstructedRoutes: urlParams.get("IncludeObstructedRoutes")?.toLowerCase() === "true",
        length: urlParams.get("Length") as unknown as number,
        medSporingskrav: urlParams.get("MedSporingskrav")?.toLowerCase() === "true",
        outputSrs: SourceReferenceSystem.EPSG_4326,
        inputSrs: urlParams.get("InputSrs") as SourceReferenceSystem,
        routeType: url.substring(url.lastIndexOf('/') + 1, url.indexOf('?')),
        startTime: dayjs(urlParams.get("StartTime")),
        veggruppe: urlParams.get("Veggruppe") as Veggruppe,
        weight: urlParams.get("Weight") as unknown as number,
        environmentName: GetEnvironmentName(url) as EnvironmentName,
        bruksklasseModulvogntog: undefined as unknown as BruksklasseModulVognTog,
        bruksklasseSpesialTransport: undefined as unknown as BruksklasseSpesialTransport,
        bruksklasseNormalTransport: undefined as unknown as BruksklasseNormalTransport,
        bruksklasseTommerTransport: undefined as unknown as BruksklasseTommerTransport
    };
    
    const { fromAddress, toAddress, viaPoints } = GetStops(urlParams.get("stops"));
    const barriers = GetBarriers(urlParams.get("Barriers"));
    
    if (!routeSettings.inputSrs || routeSettings.inputSrs === SourceReferenceSystem.EPSG_25833) {

        reprojectAddressItem(fromAddress, SourceReferenceSystem.EPSG_25833, SourceReferenceSystem.EPSG_4326);
        reprojectAddressItem(toAddress, SourceReferenceSystem.EPSG_25833, SourceReferenceSystem.EPSG_4326);
        
        for (const viaPoint of viaPoints) {
            reprojectAddressItem(viaPoint, SourceReferenceSystem.EPSG_25833, SourceReferenceSystem.EPSG_4326);
        }

        for (const barrier of barriers) {
            reprojectAddressItem(barrier, SourceReferenceSystem.EPSG_25833, SourceReferenceSystem.EPSG_4326);
        }
    };

    switch (routeSettings.routeType.toLowerCase()) {
        case "bruksklassemodulvogntog":
            routeSettings.bruksklasseModulvogntog = urlParams.get("Bruksklasse") as BruksklasseModulVognTog;
            break;
        case "bruksklassespesialtransport":
            routeSettings.bruksklasseSpesialTransport = urlParams.get("Bruksklasse") as BruksklasseSpesialTransport;
            break;
        case "bruksklassenormaltransport":
            routeSettings.bruksklasseNormalTransport = urlParams.get("Bruksklasse") as BruksklasseNormalTransport;
            break;
        case "bruksklassetommertransport":
            routeSettings.bruksklasseTommerTransport = urlParams.get("Bruksklasse") as BruksklasseTommerTransport;
    }

    const environmentName = GetEnvironmentName(url) as EnvironmentName;

    return { routeSettings, fromAddress, toAddress, viaPoints, barriers, environmentName };
}

function GetEnvironmentName(url: string) {

    const pos = getPos(url, '/', 10);
    const baseUrl = url.substring(0, pos);

    switch (baseUrl) {
        case process.env.REACT_APP_BASE_ADRESS_PROD as string:
            return "prod"
        case process.env.REACT_APP_BASE_ADRESS_ATM as string:
            return "atm"
        case process.env.REACT_APP_BASE_ADRESS_STM as string:
            return "stm"
        case process.env.REACT_APP_BASE_ADRESS_LOCAL as string:
            return "local"
        default:
            return "prod"
    }
}

function getPos(str: string, subStr: string, i: number) {
    return str.split(subStr, i).join(subStr).length;
}

function GetBarriers(queryString: string | null) {
    if (queryString === null) {
        return new Array<AddressItem>();
    }
    const stops = queryString.split(';');

    const barriers = new Array<AddressItem>();
    for (let i = 1; i < stops.length - 1; i++) {

        const coordinates = stops[i].split(',');
        const newLocation: [number, number] = [parseFloat(coordinates[0]), parseFloat(coordinates[1])];

        const barrier = {
            location: newLocation,
            name: newLocation[1] + ", " + newLocation[0],
            order: i - 1
        }
        barriers.push(barrier);
    }

    return barriers;
}

function GetStops(queryString: string | null): { fromAddress: AddressItem, toAddress: AddressItem, viaPoints: Array<AddressItem> } {

    if (queryString === null) {
        throw new Error("Faulty URL")
    }

    const stops = queryString.split(';');

    const fromCoordinates = stops[0].split(',');
    const toCoordinates = stops[stops.length - 1].split(',')

    const fromLocation: [number, number] = [parseFloat(fromCoordinates[0]), parseFloat(fromCoordinates[1])];
    const toLocation: [number, number] = [parseFloat(toCoordinates[0]), parseFloat(toCoordinates[1])];

    const fromAddress = {
        location: fromLocation,
        name: fromLocation[1] + ", " + fromLocation[0],
        order: 0
    }

    const toAddress = {
        location: toLocation,
        name:  toLocation[1] + ", " + fromLocation[0],
        order: stops.length - 1
    }

    const viaPoints = new Array<AddressItem>();
    for (let i = 1; i < stops.length - 1; i++) {
        const coordinates = stops[i].split(',');
        const newLocation: [number, number] = [parseFloat(coordinates[0]), parseFloat(coordinates[1])];

        const viaPoint = {
            location: newLocation,
            name: newLocation[1] + ", " + newLocation[0],
            order: i - 1
        }        
        viaPoints.push(viaPoint);
    }

    return { fromAddress, toAddress, viaPoints }
}

export default function ExternalRouteInput(props: IExternalRouteInputProps) {
    const { setFeedbackMessage } = useUserNotificationActions();
    
    const { setFrom, setTo, addViaPoints, addBarrierPoints } = useRouteLocationActions();
    const { setRouteSettings } = useRouteSettingsActions();
    const { setEnvironment } = useEnvironmentSettingActions();

    const [useUrlDirectly, setUseUrlDirectly] = React.useState(false);
    const [url, setUrl] = React.useState('');
    const [scrollToBottom, setScrollToBottom] = React.useState(false);
    const switchHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUseUrlDirectly(event.target.checked);
        setScrollToBottom(true);
    };
    const changeUrl = (inputString: string) => {
        setUrl(inputString);
    }

    useEffect(() => {
        if(scrollToBottom){
            var muiPaper = document.getElementById('searchMenuPaper');
            muiPaper?.scrollTo(0, muiPaper.scrollHeight);
            setScrollToBottom(false);
        }
    });

    const onClick = (event: any) => {

        if (!url) {
            return;
        }
    
        try {
            const { 
                routeSettings, 
                fromAddress, 
                toAddress, 
                viaPoints, 
                barriers, 
                environmentName 
            } = ParseRouteSettingsFromUrl(url);

            setEnvironment(environmentName);
            setRouteSettings(routeSettings);
            setFrom(fromAddress);
            setTo(toAddress);
            addViaPoints(viaPoints);
            addBarrierPoints(barriers);                
            
        }
        catch {
            setFeedbackMessage({ message: "Ugyldig URL", severity: "error" });
        }        
    }

    const updateButtonDisabled = !url || url.length === 0;

    return (
        <>
            <div style={{ display: "flex", borderBottom: "0.5px solid grey", marginBottom: "20px", marginTop: "20px" }}>
                <p>Hent parametere fra RuteplanAPI kall</p>
                <Switch onChange={switchHandler} checked={useUrlDirectly}></Switch>
            </div>

            {useUrlDirectly &&
                <>
                    <StringInput multiline={false} label='URL' value={url} onChange={(value: string) => changeUrl(value)} />
                    <Button style={{ marginTop: '20px' }} onClick={onClick} variant="contained" size="small" disabled={updateButtonDisabled}>Bruk URL</Button>
                </>
            }
        </>
    )
}